import { useQuery } from "@tanstack/react-query";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787";

export interface Project {
  id: number;
  project_name?: string;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  address?: string;
  budget?: string;
  status:
    | "draft"
    | "active"
    | "pending"
    | "completed"
    | "sent"
    | "under-review"
    | "accepted"
    | "rejected";
  created_at: string;
}

export function useDashboardData() {
  // Fetch all projects for metrics
  const {
    data: projectsData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: ["dashboard-projects"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/projects?limit=100`);
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }
      const result = await response.json();
      return result.data as Project[];
    },
  });

  // Fetch recent projects (last 5)
  const {
    data: recentProjectsData,
    isLoading: recentProjectsLoading,
    error: recentProjectsError,
  } = useQuery({
    queryKey: ["dashboard-recent-projects"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/projects?limit=5&order=created_at:desc`);
      if (!response.ok) {
        throw new Error(`Failed to fetch recent projects: ${response.status}`);
      }
      const result = await response.json();
      return result.data as Project[];
    },
  });

  // Fetch customers count and details
  const {
    data: customersData,
    isLoading: customersLoading,
    error: customersError,
  } = useQuery({
    queryKey: ["dashboard-customers"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/customers?limit=1`);
      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.status}`);
      }
      const result = await response.json();
      return result.meta;
    },
  });

  // Fetch all customers for analytics
  const {
    data: allCustomersData,
    isLoading: allCustomersLoading,
    error: allCustomersError,
  } = useQuery({
    queryKey: ["dashboard-all-customers"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/customers?limit=100`);
      if (!response.ok) {
        throw new Error(`Failed to fetch all customers: ${response.status}`);
      }
      const result = await response.json();
      return result.data;
    },
  });

  // Fetch recent customers for New Customers section
  const {
    data: recentCustomersData,
    isLoading: recentCustomersLoading,
    error: recentCustomersError,
  } = useQuery({
    queryKey: ["dashboard-recent-customers"],
    queryFn: async () => {
      const response = await fetch(
        `${apiUrl}/api/v1/customers?limit=10&order_by=created_at&order=desc`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch recent customers: ${response.status}`);
      }
      const result = await response.json();
      return result.data;
    },
  });

  const isLoading =
    projectsLoading ||
    recentProjectsLoading ||
    customersLoading ||
    allCustomersLoading ||
    recentCustomersLoading;

  const error =
    projectsError ||
    recentProjectsError ||
    customersError ||
    allCustomersError ||
    recentCustomersError;

  return {
    projectsData,
    recentProjectsData,
    customersData,
    allCustomersData,
    recentCustomersData,
    isLoading,
    error,
  };
}
